import { useEffect, useState } from "react";
import { api, fmtDate, openBlob } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageTitle, Field, SearchBox, matchQ } from "../components/common";
import { DateField } from "../components/DateFields";
import { RETURN_MOTIFS } from "../lib/labels";
import { useLang } from "../lib/i18n";

export default function Returns() {
  const { user } = useAuth();
  const { t } = useLang();
  const staff = user.role !== "soustraitant";
  const [q, setQ] = useState({ date_from: "", date_to: "", company_id: "", driver_id: "", motif: "" });
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [companies, setCompanies] = useState([]);
  const [drivers, setDrivers] = useState([]);
  useEffect(() => {
    if (staff) api.get("/companies").then((r) => setCompanies(r.data));
    api.get("/drivers").then((r) => setDrivers(r.data));
  }, [staff]);
  useEffect(() => {
    const params = Object.fromEntries(Object.entries(q).filter(([, v]) => v));
    api.get("/returns", { params }).then((r) => setRows(r.data));
  }, [q]);
  const set = (k) => (e) => setQ({ ...q, [k]: e.target.value });
  const byDate = rows.filter((r) => matchQ(search, r.declaration_ref, r.company_name, r.driver_name, r.return.motif, r.return.motif_other, r.return.parcel_ref, r.return.description))
    .reduce((acc, r) => ({ ...acc, [r.work_date]: [...(acc[r.work_date] || []), r] }), {});
  const motif = (r) => (["AUTRE", "REROUTE"].includes(r.return.motif) ? `${r.return.motif} : ${r.return.motif_other}` : r.return.motif);
  return (
    <div>
      <PageTitle title={t("ret.title")} sub={t("ret.sub")}><SearchBox value={search} onChange={setSearch} testid="returns-search" /></PageTitle>
      <div className="tl-card grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        <Field label={t("c.du")}><DateField value={q.date_from} onChange={(v) => set("date_from")({ target: { value: v } })} testid="returns-from" /></Field>
        <Field label={t("c.au")}><DateField value={q.date_to} onChange={(v) => set("date_to")({ target: { value: v } })} testid="returns-to" /></Field>
        {staff && <Field label={t("c.company")}><select className="tl-select" value={q.company_id} onChange={set("company_id")} data-testid="returns-company"><option value="">{t("c.allF")}</option>{companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</select></Field>}
        <Field label={t("c.driver")}><select className="tl-select" value={q.driver_id} onChange={set("driver_id")} data-testid="returns-driver"><option value="">{t("c.all")}</option>{drivers.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</select></Field>
        <Field label={t("ret.motif")}><select className="tl-select" value={q.motif} onChange={set("motif")} data-testid="returns-motif"><option value="">{t("c.all")}</option>{RETURN_MOTIFS.map((m) => <option key={m}>{m}</option>)}</select></Field>
      </div>
      {Object.entries(byDate).map(([day, list]) => (
        <div key={day} className="tl-card overflow-x-auto mb-4" data-testid={`returns-day-${day}`}>
          <div className="flex justify-between items-center mb-2">
            <h3 className="font-bold uppercase">{fmtDate(day)}</h3>
            <span className="text-sm text-slate-600">{list.reduce((s, r) => s + (r.return.quantity || 0), 0)} {t("ret.parcels")} · {list.length} {t("ret.lines")}</span>
          </div>
          <table className="tl-table">
            <thead><tr><th>{t("ret.decl")}</th><th>{t("ctl.compDriver")}</th><th>{t("ret.motif")}</th><th>{t("ret.qty")}</th><th>{t("ret.rank")}</th><th>{t("ret.parcel")}</th><th>{t("ret.photos")}</th></tr></thead>
            <tbody>
              {list.map((r) => (
                <tr key={r.return.id} data-testid={`return-row-${r.return.id}`}>
                  <td className="tl-mono text-xs">{r.declaration_ref}</td>
                  <td>{r.company_name}<div className="text-xs text-slate-500">{r.driver_name}</div></td>
                  <td className="font-semibold">{motif(r)}</td>
                  <td>{r.return.quantity}</td><td>{r.return.rank ?? "—"}</td>
                  <td className="tl-mono text-xs">{r.return.parcel_ref}<div className="font-sans">{r.return.description}</div></td>
                  <td>{r.return.photo_ids.map((id, i) => <button key={id} className="underline mr-2 text-sm" onClick={() => openBlob(`/files/${id}/download`)}>Photo {i + 1}</button>)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
      {!rows.length && <div className="tl-card text-slate-500" data-testid="returns-empty">{t("ret.none")}</div>}
    </div>
  );
}
